<template>
  <div class="mobile-page">
    <!-- Theme toggle on the left -->
    <div class="theme-toggle-left">
      <div class="theme-switch" :class="{ active: isDark }" @click="toggleTheme">
        <div class="theme-switch-track">
          <div class="theme-switch-thumb"></div>
        </div>
      </div>
    </div>

    <!-- Language dropdown in top right corner -->
    <div class="page-controls">
      <div class="lang-dropdown" :class="{ open: isLangDropdownOpen }">
        <button class="lang-btn" @click="toggleLangDropdown">
          {{ currentLang.toUpperCase() }}
        </button>
        <div class="lang-list">
          <button 
            v-for="locale in availableLocales" 
            :key="locale.code"
            class="lang-list-btn"
            :class="{ active: locale.code === currentLang }"
            @click="changeLanguage(locale.code)"
          >
            {{ locale.code.toUpperCase() }}
          </button>
        </div>
      </div>
    </div>

    <div class="main-container">
      <div class="center-content">
        <div class="mobile-card">
          <!-- Header inside card -->
          <div class="card-header">
            <div class="card-title">{{ $t('pickup.title') }}</div>
          </div>
          
          <div class="card-content">
            <div class="description">
              <p>{{ $t('pickup.description') }}</p>
            </div>

            <!-- QR Code Container -->
            <div class="qr-container">
              <div v-if="qrCodeDataUrl" class="qr-code-wrapper">
                <img 
                  :src="qrCodeDataUrl" 
                  :alt="$t('pickup.qrCodeAlt')"
                  class="qr-code-image"
                />
              </div>
              <div v-else class="qr-loading">
                <div class="loading-spinner"></div>
                <p>{{ $t('pickup.generating') }}</p>
              </div>
            </div>

            <div class="download-section">
              <button
                class="download-btn"
                @click="downloadQRCode"
                :disabled="!qrCodeDataUrl"
              >
                <span class="download-icon">↓</span>
                {{ $t('pickup.download') }}
              </button>
            </div>
          </div>
        </div>
        
        <!-- Logo and Copyright -->
        <LogoFooter />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import QRCode from 'qrcode'
import LogoFooter from '@/components/LogoFooter.vue'
import '@/assets/styles/mobile-pages.css'

export default {
  name: 'Pickup',
  components: {
    LogoFooter
  },
  setup() {
    const route = useRoute()
    const { locale } = useI18n()
    const code = ref('')
    const qrCodeDataUrl = ref('')
    const isLangDropdownOpen = ref(false)
    const isDark = ref(false)

    const currentLang = computed(() => locale.value)

    const availableLocales = [
      { code: 'vi', name: 'Tiếng Việt' },
      { code: 'en', name: 'English' },
      { code: 'ko', name: '한국어' },
      { code: 'ru', name: 'Русский' }
    ]

    const toggleLangDropdown = () => {
      isLangDropdownOpen.value = !isLangDropdownOpen.value
    }

    const changeLanguage = (lang) => {
      locale.value = lang
      localStorage.setItem('locale', lang)
      isLangDropdownOpen.value = false
    }

    const toggleTheme = () => {
      isDark.value = !isDark.value
      localStorage.setItem('theme', isDark.value ? 'dark' : 'light')
      updateTheme()
    }

    const updateTheme = () => {
      if (isDark.value) {
        document.documentElement.classList.add('dark')
        document.documentElement.classList.remove('light')
      } else {
        document.documentElement.classList.add('light')
        document.documentElement.classList.remove('dark')
      }
    }

    const handleClickOutside = (event) => {
      if (!event.target.closest('.lang-dropdown')) {
        isLangDropdownOpen.value = false
      }
    }

    const generateQRCode = async () => {
      try {
        // Generate QR code with the code
        const qrDataUrl = await QRCode.toDataURL(code.value, {
          width: 300,
          margin: 2,
          color: {
            dark: '#4f3491', // Secondary color (purple)
            light: '#FFFFFF'
          },
          errorCorrectionLevel: 'M'
        })
        qrCodeDataUrl.value = qrDataUrl
      } catch (error) {
        console.error('Error generating QR code:', error)
      }
    }

    const downloadQRCode = () => {
      if (qrCodeDataUrl.value) {
        const link = document.createElement('a')
        link.download = `pickup-qr-${code.value}.png`
        link.href = qrCodeDataUrl.value
        link.click()
      }
    }

    onMounted(() => {
      document.addEventListener('click', handleClickOutside)
      
      // Get the code from the route parameters
      code.value = route.params.code
      if (code.value) {
        generateQRCode()
      }

      // Set initial language from localStorage or detect browser language, if not found, detect system language
      const savedLang = localStorage.getItem('locale')
      if (savedLang && availableLocales.find(l => l.code === savedLang)) {
        locale.value = savedLang
      } else {
        const browserLang = navigator.language || navigator.userLanguage
        const langCode = browserLang.split('-')[0].toLowerCase()
        if (availableLocales.find(l => l.code === langCode)) {
          locale.value = langCode
        }
      }

      // Set initial theme from localStorage or detect system preference, if not found, detect system preference
      const savedTheme = localStorage.getItem('theme')
      if (savedTheme) {
        isDark.value = savedTheme === 'dark'
      } else {
        isDark.value = window.matchMedia('(prefers-color-scheme: dark)').matches
      }
      updateTheme()
    })

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)
    })

    return {
      code,
      qrCodeDataUrl,
      downloadQRCode,
      isLangDropdownOpen,
      currentLang,
      availableLocales,
      toggleLangDropdown,
      changeLanguage,
      isDark,
      toggleTheme
    }
  }
}
</script>

<style scoped>
/* Pickup page specific styles - only unique styles that don't exist in mobile-pages.css */
</style>
